import type { DemoCustomer, DemoOrder, HistoryEntry, PanelSectionContent } from "@/pages/painel/types";

export const PANEL_MENU = [
  { id: "visao-geral", label: "Visão geral", icon: "ri-dashboard-line" },
  { id: "ficha", label: "Ficha do site", icon: "ri-file-list-3-line" },
  { id: "editar-site", label: "Editar site", icon: "ri-edit-2-line" },
  { id: "categorias", label: "Categorias", icon: "ri-apps-2-line" },
  { id: "catalogo", label: "Catálogo", icon: "ri-walk-line" },
  { id: "clientes", label: "Clientes", icon: "ri-user-heart-line" },
  { id: "pedidos", label: "Pedidos", icon: "ri-shopping-bag-3-line" },
  { id: "entregas", label: "Entregas", icon: "ri-truck-line" },
  { id: "whatsapp", label: "WhatsApp", icon: "ri-whatsapp-line" },
  { id: "configuracoes", label: "Configurações", icon: "ri-settings-3-line" },
  { id: "seo", label: "SEO e compartilhamento", icon: "ri-global-line" },
  { id: "historico", label: "Histórico", icon: "ri-history-line" },
] as const;

/** Seções da página inicial, na ordem exata de cima para baixo. */
export const siteSections: PanelSectionContent[] = [
  {
    id: "topbar",
    name: "Faixa superior de avisos",
    position: 1,
    description: "Faixa de mensagens rotativas no topo do site, em café quase preto.",
    texts: [
      { label: "Mensagem 1", value: "Atendimento personalizado pelo WhatsApp" },
      { label: "Mensagem 2", value: "Escolha seus favoritos e finalize com facilidade" },
      { label: "Mensagem 3", value: "Calçados e acessórios para todos os seus momentos" },
      { label: "Mensagem 4", value: "Entrega e retirada combinadas no atendimento" },
    ],
    images: [],
    colors: [
      { label: "Fundo da faixa", value: "#211C19 (café quase preto)" },
      { label: "Texto", value: "#FFFDF9 (branco quente)" },
      { label: "Separadores", value: "#C5A05A (champagne)" },
    ],
    links: [],
    desktop: "Mensagens em rolagem contínua e discreta.",
    mobile: "Mesmas mensagens, com fonte reduzida.",
    lastChange: "20/09/2026",
    canRestore: true,
  },
  {
    id: "header",
    name: "Cabeçalho e menu",
    position: 2,
    description: "Logo, navegação por categorias e atalhos de busca, favoritos e sacola.",
    texts: [
      { label: "Marca", value: "Auréa" },
      { label: "Complemento", value: "Calçados & Acessórios" },
    ],
    images: [],
    colors: [
      { label: "Marca", value: "foreground-900 (café espresso)" },
      { label: "Fundo ao rolar", value: "background-50 (branco quente)" },
    ],
    links: [
      { label: "Novidades", href: "/novidades" },
      { label: "Sapatos", href: "/categoria/sandalias" },
      { label: "Bolsas", href: "/categoria/bolsas" },
      { label: "Acessórios", href: "/categoria/cintos" },
    ],
    desktop: "Navegação completa em linha, com menus Sapatos e Acessórios.",
    mobile: "Menu em lateral com rolagem interna e botão de fechar sempre visível.",
    lastChange: "20/09/2026",
    canRestore: true,
  },
  {
    id: "hero",
    name: "Hero principal",
    position: 3,
    description: "Carrossel full-bleed de campanhas fotográficas, sem nenhum texto sobre a imagem.",
    texts: [],
    images: [
      {
        label: "Campanha 01 — Cidade em movimento (desktop)",
        url: "https://readdy.ai/api/search-image?query=Editorial%20fashion%20campaign%20photograph%20of%20an%20elegant%20Brazilian%20woman%20in%20natural%20motion%20walking%20through%20contemporary%20urban%20architecture%20with%20light%20concrete%20walls%2C%20wearing%20a%20sleek%20black%20and%20graphite%20tailored%20suit%2C%20an%20intense%20red%20leather%20slingback%20heel%20as%20the%20clear%20protagonist%20in%20sharp%20focus%2C%20full%20shoe%20visible%20and%20uncropped%2C%20realistic%20leather%20texture%2C%20professional%20advertising%20lighting%2C%20cinematic%20depth%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark%2C%20no%20graphic%20elements&width=1920&height=900&seq=aur-camp-cidade-desk&orientation=landscape",
      },
      {
        label: "Campanha 02 — Couro e luz (desktop)",
        url: "https://readdy.ai/api/search-image?query=Editorial%20fashion%20campaign%20photograph%20shot%20on%20location%20in%20bright%20white%20stone%20architecture%20with%20strong%20natural%20sunlight%20and%20defined%20shadows%2C%20a%20Brazilian%20woman%20wearing%20a%20sophisticated%20caramel%20leather%20flat%20sandal%20with%20an%20elegant%20modern%20design%20clearly%20visible%20and%20in%20focus%2C%20warm%20golden%20daylight%2C%20stone%20white%20and%20caramel%20palette%2C%20realistic%20premium%20materials%2C%20professional%20campaign%20photography%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark%2C%20no%20graphic%20elements&width=1920&height=900&seq=aur-camp-couro-desk&orientation=landscape",
      },
      {
        label: "Campanha 03 — Noite urbana (desktop)",
        url: "https://readdy.ai/api/search-image?query=Sophisticated%20night%20editorial%20fashion%20campaign%20photograph%20in%20a%20contemporary%20urban%20setting%2C%20an%20elegant%20woman%20wearing%20a%20black%20leather%20stiletto%20court%20shoe%20as%20the%20protagonist%20clearly%20visible%20and%20uncropped%2C%20a%20small%20silver%20metallic%20handbag%20as%20a%20subtle%20complement%2C%20black%20and%20charcoal%20tones%20with%20discreet%20points%20of%20soft%20white%20light%2C%20moody%20cinematic%20lighting%2C%20realistic%20leather%20and%20metal%20textures%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark%2C%20no%20graphic%20elements&width=1920&height=900&seq=aur-camp-noite-desk&orientation=landscape",
      },
      {
        label: "Campanha 04 — Casual contemporâneo (desktop)",
        url: "https://readdy.ai/api/search-image?query=Bright%20minimalist%20editorial%20lifestyle%20photograph%20of%20a%20Brazilian%20woman%20in%20natural%20movement%20wearing%20off-white%20women%E2%80%99s%20sneakers%20clearly%20visible%20and%20in%20focus%20as%20the%20main%20subject%2C%20straight%20blue%20jeans%20and%20a%20white%20shirt%2C%20modern%20clean%20urban%20background%20in%20light%20grey%20concrete%2C%20white%20and%20soft%20grey%20palette%20with%20small%20black%20details%2C%20realistic%20fabric%20and%20shoe%20textures%2C%20professional%20advertising%20lighting%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark%2C%20no%20graphic%20elements&width=1920&height=900&seq=aur-camp-casual-desk&orientation=landscape",
      },
      {
        label: "Campanha 05 — Verão brasileiro sofisticado (desktop)",
        url: "https://readdy.ai/api/search-image?query=Sophisticated%20summer%20editorial%20fashion%20campaign%20photograph%20of%20a%20Brazilian%20woman%20wearing%20a%20low%20metallic%20champagne%20leather%20flat%20sandal%20as%20the%20focus%2C%20clearly%20visible%20and%20uncropped%2C%20light%20elegant%20summer%20outfit%2C%20outdoor%20contemporary%20architecture%20with%20natural%20stone%20and%20discreet%20deep%20green%20vegetation%2C%20natural%20daylight%2C%20white%20and%20stone%20palette%20with%20champagne%20metal%20accents%2C%20realistic%20materials%2C%20premium%20campaign%20photography%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark%2C%20no%20graphic%20elements&width=1920&height=900&seq=aur-camp-verao-desk&orientation=landscape",
      },
    ],
    colors: [{ label: "Transição", value: "fade com leve deslocamento horizontal" }],
    links: [],
    desktop: "Full-bleed, 82svh, 5 campanhas 1920 × 900, setas e indicadores discretos.",
    mobile: "74svh, 5 campanhas verticais 900 × 1200 próprias, swipe e indicadores.",
    lastChange: "20/09/2026",
    canRestore: true,
  },
  {
    id: "beneficios",
    name: "Benefícios",
    position: 4,
    description: "Quatro destaques de atendimento, compra, entrega e numeração.",
    texts: [
      { label: "Destaque 1", value: "Atendimento próximo" },
      { label: "Destaque 2", value: "Compra facilitada pelo WhatsApp" },
      { label: "Destaque 3", value: "Entrega e retirada combinadas" },
      { label: "Destaque 4", value: "Ajuda para escolher modelo e numeração" },
    ],
    images: [],
    colors: [{ label: "Ícones", value: "primary-600 (bordô cereja)" }],
    links: [],
    desktop: "Quatro colunas alinhadas.",
    mobile: "Uma coluna, com respiro adequado.",
    lastChange: "20/09/2026",
    canRestore: true,
  },
  {
    id: "categorias",
    name: "Categorias",
    position: 5,
    description: "Vitrine das categorias de calçados, bolsas e acessórios, cada uma com card próprio.",
    texts: [{ label: "Título da seção", value: "Encontre seu passo por categoria" }],
    images: [],
    colors: [{ label: "Cartões", value: "background-100 (marfim)" }],
    links: [
      { label: "Sandálias", href: "/categoria/sandalias" },
      { label: "Scarpins", href: "/categoria/scarpins" },
      { label: "Sapatilhas", href: "/categoria/sapatilhas" },
      { label: "Mules", href: "/categoria/mules" },
      { label: "Mocassins", href: "/categoria/mocassins" },
      { label: "Tênis", href: "/categoria/tenis" },
      { label: "Rasteiras", href: "/categoria/rasteiras" },
      { label: "Papetes", href: "/categoria/papetes" },
      { label: "Botas", href: "/categoria/botas" },
      { label: "Bolsas", href: "/categoria/bolsas" },
      { label: "Cintos", href: "/categoria/cintos" },
      { label: "Semijoias", href: "/categoria/semijoias" },
    ],
    desktop: "Grade de cartões retrato, calçados primeiro, depois bolsas e acessórios.",
    mobile: "Grade de duas colunas.",
    lastChange: "20/09/2026",
    canRestore: true,
  },
  {
    id: "selecao",
    name: "Seleção Auréa",
    position: 6,
    description: "Destaque de produtos selecionados do catálogo.",
    texts: [{ label: "Título da seção", value: "Seleção Auréa" }],
    images: [],
    colors: [{ label: "Cartões", value: "background-50 (branco quente)" }],
    links: [{ label: "Ver novidades", href: "/novidades" }],
    desktop: "Grade de produtos em destaque.",
    mobile: "Grade adaptada com rolagem confortável.",
    lastChange: "20/09/2026",
    canRestore: true,
  },
  {
    id: "editorial",
    name: "Banner editorial",
    position: 7,
    description: "Faixa editorial com imagem de fundo e chamada para a coleção.",
    texts: [
      { label: "Selo", value: "Editorial Auréa" },
      { label: "Título", value: "Elegância que começa nos seus passos" },
      {
        label: "Subtítulo",
        value:
          "Modelos versáteis, texturas marcantes e acessórios que completam produções para diferentes momentos da vida real.",
      },
      { label: "Botão", value: "Descobrir a coleção" },
    ],
    images: [
      {
        label: "Imagem de fundo",
        url: "https://readdy.ai/api/search-image?query=Editorial%20horizontal%20still%20life%20of%20three%20different%20womens%20shoes%2C%20a%20structured%20leather%20handbag%20and%20delicate%20gold%20jewelry%20arranged%20on%20a%20warm%20ivory%20surface%20with%20a%20deep%20bordeaux%20accent%20base%2C%20editorial%20lighting%2C%20true%20leather%20texture%2C%20generous%20empty%20ivory%20space%20on%20the%20left%20for%20text%2C%20no%20people%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=1600&height=900&seq=aur-banner-editorial-01&orientation=landscape&nocache=true",
      },
    ],
    colors: [{ label: "Selo", value: "accent-300 (champagne)" }],
    links: [{ label: "Descobrir a coleção", href: "#selecao" }],
    desktop: "Imagem full-bleed com texto à esquerda.",
    mobile: "Texto sobreposto com contraste reforçado.",
    lastChange: "20/09/2026",
    canRestore: true,
  },
  {
    id: "looks",
    name: "Momentos",
    position: 8,
    description: "Quatro momentos com pares de imagens de composição.",
    texts: [
      { label: "Momento 1", value: "Para trabalhar" },
      { label: "Momento 2", value: "Para viver a cidade" },
      { label: "Momento 3", value: "Para celebrar" },
      { label: "Momento 4", value: "Para aproveitar o fim de semana" },
    ],
    images: [],
    colors: [{ label: "Cartões", value: "background-100 (marfim)" }],
    links: [],
    desktop: "Quatro blocos com pares de imagens.",
    mobile: "Blocos empilhados com imagens lado a lado.",
    lastChange: "20/09/2026",
    canRestore: true,
  },
  {
    id: "novidades",
    name: "Novidades",
    position: 9,
    description: "Produtos recém-chegados, ligados ao mesmo catálogo único.",
    texts: [{ label: "Título da seção", value: "Acabaram de chegar" }],
    images: [],
    colors: [{ label: "Selo Novo", value: "secondary-500 (cinza quente)" }],
    links: [{ label: "Ver novidades", href: "/novidades" }],
    desktop: "Grade de produtos marcados como novos.",
    mobile: "Grade com duas colunas.",
    lastChange: "20/09/2026",
    canRestore: true,
  },
  {
    id: "atendimento",
    name: "Atendimento",
    position: 10,
    description: "Seção de atendimento humanizado com imagem e lista de apoio.",
    texts: [
      { label: "Título", value: "Precisa de ajuda para escolher?" },
      {
        label: "Texto",
        value:
          "Conte para a gente o que você procura. Ajudamos a encontrar o modelo, a numeração e os acessórios que combinam com o seu momento.",
      },
      { label: "Botão", value: "Falar pelo WhatsApp" },
    ],
    images: [
      {
        label: "Imagem",
        url: "https://readdy.ai/api/search-image?query=Warm%20realistic%20lifestyle%20photo%20of%20a%20Brazilian%20store%20attendant%20presenting%20different%20womens%20shoes%20on%20an%20elegant%20display%20table%20with%20several%20distinct%20models%2C%20a%20client%20looking%20at%20a%20bordeaux%20sandal%20and%20a%20white%20sneaker%2C%20cozy%20neutral%20boutique%20with%20sand%20interiors%20and%20leather%20textures%2C%20natural%20daylight%2C%20friendly%20genuine%20expressions%2C%20no%20clothing%20rack%2C%20no%20hangers%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=1000&height=800&seq=aur-atendimento-01&orientation=landscape&nocache=true",
      },
    ],
    colors: [{ label: "Selo", value: "accent-700 (champagne)" }],
    links: [{ label: "Falar pelo WhatsApp", href: "https://wa.me/message/6X77HRWZGKPQH1" }],
    desktop: "Imagem e texto lado a lado.",
    mobile: "Imagem acima do texto.",
    lastChange: "20/09/2026",
    canRestore: true,
  },
  {
    id: "historia",
    name: "História da marca",
    position: 11,
    description: "Nossa essência, com imagem e três pilares.",
    texts: [
      { label: "Selo", value: "Nossa essência" },
      { label: "Título", value: "Estilo, conforto e personalidade em cada escolha" },
      { label: "Pilar 1", value: "Versatilidade — escolhas para diferentes momentos" },
      { label: "Pilar 2", value: "Cuidado — atenção aos materiais e acabamentos" },
      { label: "Pilar 3", value: "Proximidade — atendimento humano pelo WhatsApp" },
    ],
    images: [
      {
        label: "Imagem",
        url: "https://readdy.ai/api/search-image?query=Editorial%20behind%20the%20scenes%20photo%20of%20a%20warm%20contemporary%20Brazilian%20atelier%20shelf%20neatly%20organized%20with%20different%20womens%20leather%20shoes%2C%20sandals%20and%20bags%20in%20caramel%2C%20ivory%20and%20bordeaux%20tones%2C%20a%20hand%20carefully%20arranging%20a%20sandal%2C%20neutral%20shoe%20boxes%20without%20branding%2C%20a%20leather%20belt%20detail%2C%20natural%20daylight%2C%20realistic%20material%20textures%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=900&height=1100&seq=aur-historia-bastidores-01&orientation=portrait&nocache=true",
      },
    ],
    colors: [{ label: "Fundo", value: "background-100 (marfim)" }],
    links: [],
    desktop: "Texto e imagem em duas colunas.",
    mobile: "Imagem acima do texto.",
    lastChange: "20/09/2026",
    canRestore: true,
  },
  {
    id: "inspiracoes",
    name: "Inspirações",
    position: 12,
    description: "Galeria com oito imagens em proporções quadrada e vertical.",
    texts: [{ label: "Título da seção", value: "Inspirações Auréa" }],
    images: [],
    colors: [{ label: "Fundo", value: "background-50 (branco quente)" }],
    links: [],
    desktop: "Mosaico com imagens quadradas e verticais.",
    mobile: "Mosaico adaptado em duas colunas.",
    lastChange: "20/09/2026",
    canRestore: true,
  },
  {
    id: "vellora",
    name: "Chamada Vellora",
    position: 13,
    description: "Faixa institucional com a chamada comercial da Vellora Studio.",
    texts: [
      { label: "Selo", value: "Projeto demonstrativo" },
      { label: "Título", value: "Gostou desta experiência para o seu negócio?" },
      { label: "Botão 1", value: "Quero uma loja para minha marca" },
      { label: "Botão 2", value: "Conhecer a Vellora Studio" },
      { label: "Aviso", value: "Modelo demonstrativo criado pela Vellora Studio." },
    ],
    images: [],
    colors: [{ label: "Fundo da faixa", value: "secondary-100 (cinza quente claro)" }],
    links: [
      { label: "Quero uma loja para minha marca", href: "https://wa.me/message/6X77HRWZGKPQH1" },
      { label: "Conhecer a Vellora Studio", href: "https://vellorastudio.suapagina.net.br/" },
    ],
    desktop: "Cartão centralizado com dois botões.",
    mobile: "Botões empilhados.",
    lastChange: "20/09/2026",
    canRestore: true,
  },
  {
    id: "rodape",
    name: "Rodapé",
    position: 14,
    description: "Institucional, categorias, atendimento, aviso demonstrativo e créditos.",
    texts: [
      { label: "Assinatura", value: "Passos que acompanham cada versão de você." },
      { label: "Aviso", value: "Modelo demonstrativo criado pela Vellora Studio." },
      { label: "Créditos", value: "Desenvolvido pela Vellora Studio" },
    ],
    images: [],
    colors: [{ label: "Fundo", value: "foreground-950 (café espresso)" }],
    links: [
      { label: "Política de privacidade", href: "modal" },
      { label: "Desenvolvido pela Vellora Studio", href: "https://vellorastudio.suapagina.net.br/" },
    ],
    desktop: "Quatro colunas com aviso institucional.",
    mobile: "Colunas empilhadas.",
    lastChange: "20/09/2026",
    canRestore: true,
  },
];

/* ------------------------------------------------------------------ */
/* Registros demonstrativos (mascarados no modo público)               */
/* ------------------------------------------------------------------ */

export const demoCustomers: DemoCustomer[] = [
  {
    code: "AUR-CLI-00001",
    name: "Marina S.",
    phone: "(62) 9****-1234",
    email: "ma***@email.com",
    city: "Goiânia",
    state: "GO",
    orders: 3,
    totalSpent: 869.7,
    lastOrderAt: "18/09/2026",
    status: "recorrente",
  },
  {
    code: "AUR-CLI-00002",
    name: "Juliana R.",
    phone: "(62) 9****-5678",
    email: "ju***@email.com",
    city: "Goiânia",
    state: "GO",
    orders: 1,
    totalSpent: 289.9,
    lastOrderAt: "14/09/2026",
    status: "novo",
  },
  {
    code: "AUR-CLI-00003",
    name: "Camila F.",
    phone: "(64) 9****-9012",
    email: "ca***@email.com",
    city: "Rio Verde",
    state: "GO",
    orders: 2,
    totalSpent: 588.6,
    lastOrderAt: "09/09/2026",
    status: "recorrente",
  },
  {
    code: "AUR-CLI-00004",
    name: "Beatriz M.",
    phone: "(11) 9****-3456",
    email: "be***@email.com",
    city: "São Paulo",
    state: "SP",
    orders: 1,
    totalSpent: 249.9,
    lastOrderAt: "30/08/2026",
    status: "inativo",
  },
];

export const demoOrders: DemoOrder[] = [
  {
    code: "AUR-PED-00003",
    date: "18/09/2026",
    time: "15:42",
    customerName: "Marina S.",
    customerPhone: "(62) 9****-1234",
    items: [
      { name: "Scarpin Slingback", code: "AUR-SCA-001", color: "Vermelho cereja", size: "36", quantity: 1, unitPrice: 289.9 },
      { name: "Rasteira Minimalista", code: "AUR-RAS-001", color: "Nude", size: "37", quantity: 1, unitPrice: 149.9 },
    ],
    subtotal: 439.8,
    shipping: 0,
    discount: 0,
    total: 439.8,
    delivery: "Entrega local",
    address: "Rua das Flores, *** — Setor Bueno, Goiânia — GO",
    status: "Aguardando confirmação",
    paymentStatus: "A confirmar",
    deliveryStatus: "A combinar",
  },
  {
    code: "AUR-PED-00002",
    date: "14/09/2026",
    time: "10:18",
    customerName: "Juliana R.",
    customerPhone: "(62) 9****-5678",
    items: [
      { name: "Sandália Salto Bloco com Tiras", code: "AUR-SAN-002", color: "Bordô", size: "37", quantity: 1, unitPrice: 259.9 },
    ],
    subtotal: 259.9,
    shipping: 15,
    discount: 0,
    total: 274.9,
    delivery: "Entrega local",
    address: "Av. Central, *** — Centro, Goiânia — GO",
    status: "Confirmado",
    paymentStatus: "A confirmar",
    deliveryStatus: "A combinar",
  },
  {
    code: "AUR-PED-00001",
    date: "09/09/2026",
    time: "19:05",
    customerName: "Camila F.",
    customerPhone: "(64) 9****-9012",
    items: [
      { name: "Mocassim Clássico", code: "AUR-MOC-001", color: "Café", size: "38", quantity: 1, unitPrice: 249.9 },
      { name: "Bolsa Transversal", code: "AUR-BOL-003", color: "Azul profundo", size: "Único", quantity: 1, unitPrice: 269.9 },
    ],
    subtotal: 519.8,
    shipping: 24.9,
    discount: 0,
    total: 544.7,
    delivery: "Correios — PAC",
    address: "Rua *** — Setor Oeste, Rio Verde — GO · CEP *****-000",
    status: "Enviado",
    paymentStatus: "Pago",
    deliveryStatus: "Em trânsito",
  },
];

export const historyEntries: HistoryEntry[] = [
  {
    id: "h1",
    field: "Hero principal › Campanha 01",
    previous: "Composição antiga em card arredondado com texto sobreposto",
    next: "Fotografia full-bleed da campanha Cidade em movimento",
    date: "20/09/2026",
    time: "11:20",
    author: "Vellora Studio",
  },
  {
    id: "h2",
    field: "Faixa superior › Cor de fundo",
    previous: "bordô cereja",
    next: "café quase preto #211C19",
    date: "20/09/2026",
    time: "10:48",
    author: "Vellora Studio",
  },
  {
    id: "h3",
    field: "SEO › Indexação",
    previous: "index, follow",
    next: "noindex, nofollow (piloto)",
    date: "20/09/2026",
    time: "10:05",
    author: "Vellora Studio",
  },
  {
    id: "h4",
    field: "Categoria Botas › Hero de categoria",
    previous: "Sem banner próprio",
    next: "Banner desktop 1920 × 760 e mobile 900 × 1200 próprios",
    date: "19/09/2026",
    time: "17:12",
    author: "Vellora Studio",
  },
];

/* ------------------------------------------------------------------ */
/* Textos de apoio                                                      */
/* ------------------------------------------------------------------ */

export const seoConfig = {
  title: "Auréa — Calçados Femininos e Acessórios",
  description:
    "Sandálias, scarpins, sapatilhas, mules, mocassins, tênis, rasteiras, papetes e botas, além de bolsas, cintos e semijoias. Escolha seus favoritos e finalize pelo WhatsApp.",
  canonical: "https://aurea.exemplo.com/",
  ogImage:
    "https://readdy.ai/api/search-image?query=Editorial%20horizontal%20still%20life%20of%20distinct%20womens%20footwear%20on%20a%20warm%20ivory%20surface%20with%20a%20deep%20coffee%20and%20champagne%20backdrop%2C%20a%20bordeaux%20slingback%20heel%2C%20a%20caramel%20leather%20sandal%20and%20a%20white%20and%20grey%20sneaker%2C%20plus%20a%20structured%20cognac%20handbag%2C%20realistic%20side%20lighting%2C%20true%20leather%20texture%2C%20premium%20commercial%20composition%2C%20no%20people%2C%20no%20text%2C%20no%20logos%2C%20no%20watermark&width=1200&height=630&seq=aur-og-share-02&orientation=landscape",
  favicon: "Monograma A em café quase preto e champagne",
  robots: "noindex, nofollow",
};

export const storeSettings = {
  name: "Auréa",
  slogan: "Passos que acompanham cada versão de você.",
  complement: "Calçados & Acessórios",
  phone: "(62) 99802-3960",
  whatsapp: "+55 62 99802-3960",
  instagram: "@vellora__studio",
  address: "Goiânia — GO · Atendimento digital em todo o Brasil",
  hours: "Segunda a sábado, das 9h às 19h (atendimento digital)",
  institutionalText:
    "A Auréa é uma marca demonstrativa de calçados femininos e acessórios, pensada para mulheres que vivem diferentes rotinas, ocasiões e versões de si mesmas.",
  privacyPolicy: "Política de privacidade demonstrativa disponível no rodapé e no checkout.",
  exchangePolicy: "Troca orientada pela equipe pelo WhatsApp, conforme combinado no atendimento.",
  freeShippingValue: "R$ 500,00 (entrega local em Goiânia, demonstrativo)",
};

export const catalogCollectedData = [
  "Nome completo",
  "WhatsApp",
  "E-mail (opcional)",
  "Cidade",
  "Estado",
  "Forma de recebimento",
  "Endereço (quando entrega)",
  "CEP (Correios e transportadora)",
  "Ponto de referência (opcional)",
  "Observações do pedido (opcional)",
  "Consentimento para uso dos dados",
];

/** Níveis de acesso do painel. */
export const panelAccessLevels = [
  {
    id: "visualizacao",
    label: "Visualização demonstrativa",
    description:
      "Aberta para apresentação do portfólio. Permite navegar por todas as seções, sem alterar dados globais. Exibe aviso de modo de visualização.",
  },
  {
    id: "teste",
    label: "Modo de teste local",
    description:
      "Alterações salvas somente no navegador (localStorage), visíveis no site por aqui. Sem credenciais. Botão para restaurar os dados originais.",
  },
  {
    id: "admin",
    label: "Administração Vellora e cliente",
    description:
      "Estrutura preparada para o backend, com os perfis vellora_admin e cliente_editor. Nenhuma credencial fictícia é liberada enquanto o backend não estiver conectado.",
  },
] as const;

export const panelRoles = [
  { id: "vellora_admin", label: "vellora_admin", description: "Administração total do projeto." },
  { id: "cliente_editor", label: "cliente_editor", description: "Edição do conteúdo da própria marca." },
] as const;

export const panelModes = {
  viewNotice:
    "Modo de visualização — navegue por todas as seções. Para editar, ative o modo de teste ou entre na administração.",
  adminNotice: "Modo administração Auréa — edição liberada.",
  testNotice:
    "Modo de teste local — as alterações ficam salvas apenas neste navegador e podem aparecer no site por aqui.",
};